import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import { Button, FormGroup, Input, Label } from 'reactstrap'
import { updateInterview } from 'store/Recruitment/actions'
import AxiosInstance from 'utils/AxiosInstance'

const ExitedModal = ({
  onFinish,
  theme,
  logoLight,
  jobTitle,
  interviewId,
  hasAppeal,
  applicant,
}) => {
  const dispatch = useDispatch()
  const [appealText, setAppealText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [appealSubmitted, setAppealSubmitted] = useState(false)

  const { user } = useSelector((store) => store.auth)
  const handleSubmitAppeal = async () => {
    if (!appealText.trim()) {
      alert('Please write your appeal message before submitting.')
      return
    }

    setIsSubmitting(true)
    try {
      // Submit the appeal to the backend
      dispatch(
        updateInterview(
          interviewId,
          { appeal: appealText.trim() },
          user === null && `?applicant=${applicant}`
        )
      )

      setAppealSubmitted(true)
      // Don't navigate away automatically - let user stay to read the success message
    } catch (error) {
      console.error('Appeal submission failed:', error)
      alert('Failed to submit appeal. Please try again or contact HR directly.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='ai-interview-container'>
      {/* Same header as interview interface */}
      <div className='d-flex align-items-center justify-content-between w-100 position-fixed interview-header'>
        <div className='d-flex align-items-center'>
          <img
            role='button'
            onClick={onFinish}
            className='light-image ml-4'
            style={{ height: '50px' }}
            src={
              theme?.loginLogo && theme.loginLogo.url
                ? theme.loginLogo.url
                : logoLight
            }
            alt=''
          />
          <div className='job-title-header'>
            <h1 className='text-white m-0'>
              {jobTitle
                ? `AI Interview - ${jobTitle}`
                : 'AI Interview Interrupted'}
            </h1>
          </div>
        </div>
      </div>

      {/* Main content with same styling as interview */}
      <div
        className='d-flex flex-column justify-content-center align-items-center min-vh-100 text-white p-4'
        style={{ paddingTop: '100px' }}
      >
        <div className='text-center' style={{ maxWidth: '700px' }}>
          {!appealSubmitted && !hasAppeal ? (
            <>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                size='4x'
                className='text-warning mb-4'
              />

              <div className='bg-warning p-4 rounded mb-4'>
                <h4 className='text-dark mb-3'>Interview Not Completed</h4>
                <p className='mb-0 text-dark'>
                  Your interview session was ended before completion due to
                  technical issues, policy violations, or early exit.
                </p>
              </div>

              <div className='bg-info p-4 rounded mb-4'>
                <h4 className='text-white mb-3'>Submit an Appeal</h4>
                <p className='text-white mb-3'>
                  If you believe this was unfair or due to technical issues
                  beyond your control, please explain your situation below and
                  we'll review your case for a second opportunity.
                </p>

                <FormGroup className='text-left'>
                  <Label className='text-white font-weight-bold'>
                    Explain what happened (optional but recommended):
                  </Label>
                  <Input
                    type='textarea'
                    rows={5}
                    placeholder='Please describe what happened during your interview session. For example: "My internet connection was unstable and caused the camera to disconnect" or "I accidentally pressed Alt+Tab which exited fullscreen mode"...'
                    value={appealText}
                    onChange={(e) => setAppealText(e.target.value)}
                    disabled={isSubmitting}
                  />
                  <small className='text-white-50 mt-1 d-block'>
                    This helps our team understand if technical issues caused
                    the interruption.
                  </small>
                </FormGroup>
              </div>

              <div className='text-center' style={{ gap: '1rem' }}>
                <div
                  className='d-flex justify-content-center'
                  style={{ gap: '1rem' }}
                >
                  <Button
                    color='success'
                    size='lg'
                    onClick={handleSubmitAppeal}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className='spinner-border spinner-border-sm mr-2'></span>
                        Submitting...
                      </>
                    ) : (
                      'Submit Appeal & Request Review'
                    )}
                  </Button>
                  <Button
                    color='secondary'
                    size='lg'
                    onClick={onFinish}
                    disabled={isSubmitting}
                  >
                    Return to Homepage
                  </Button>
                </div>
                <p className='text-muted mt-3 small'>
                  You can also contact HR directly if you prefer not to use this
                  form.
                </p>
              </div>
            </>
          ) : (
            <>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                size='4x'
                className='text-success mb-4'
              />

              <div className='bg-success p-4 rounded mb-4'>
                <h4 className='text-white mb-3'>
                  Appeal Submitted Successfully
                </h4>
                <p className='mb-0 text-white'>
                  Thank you for your appeal. Our team will review your case and
                  contact you soon.
                </p>
              </div>

              <div className='bg-secondary p-4 rounded mb-4'>
                <h4 className='text-info mb-3'>What's Next?</h4>
                <ul className='list-unstyled text-dark mb-0'>
                  <li>
                    📝 Your appeal has been{' '}
                    <strong>submitted for review</strong>
                  </li>
                  <li>
                    👥 Our HR team will <strong>evaluate your case</strong>
                  </li>
                  <li>
                    📧 You'll be{' '}
                    <strong>contacted within 2-3 business days</strong>
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ExitedModal
